import React, { useState, useEffect } from 'react';
import SectionSelector from './SectionSelector';
import FormInput from './components/FormInput';
import FormTextarea from './components/FormTextarea';
import AddButton from './components/AddButton';
import RemoveButton from './components/RemoveButton';
import WebsiteLinksInput from './components/WebsiteLinksInput';
import CategorizedSkillsInput from './components/CategorizedSkillsInput';
import ExperiencesInput from './components/ExperiencesInput';

const newlineJoin = (values = []) => (Array.isArray(values) ? values.join('\n') : '');
const splitLines = (value = '') =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

const ProfessionalResumeForm = ({
  resumeData,
  onUpdate,
  validationErrors = {},
  onFieldFocus,
  onFieldBlur,
  getDynamicSections,
  renderDynamicSectionForm,
  templateId = 'professional',
}) => {
  const [activeSection, setActiveSection] = useState('contact');
  const [newCert, setNewCert] = useState('');
  const [newWebsite, setNewWebsite] = useState("");
  const [newTechnology, setNewTechnology] = useState("");
  
  // Function to categorize skills from key_skills into structured format
  const categorizeSkills = (keySkills) => {
    if (!Array.isArray(keySkills)) return {};
    
    const categorized = {
      programming_languages: [],
      frameworks: [],
      tools: [],
      ai_ml_technologies: []
    };

    keySkills.forEach(skill => {
      const skillLower = skill.toLowerCase();
      if (skillLower.includes('python') || skillLower.includes('java') || skillLower.includes('javascript') || 
          skillLower.includes('c++') || skillLower.includes('sql') || skillLower.includes('html') || 
          skillLower.includes('css') || skillLower.includes('php') || skillLower.includes('ruby') ||
          skillLower.includes('go') || skillLower.includes('rust') || skillLower.includes('swift') ||
          skillLower.includes('c#') || skillLower.includes('kotlin') || skillLower.includes('scala')) {
        categorized.programming_languages.push(skill);
      } else if (skillLower.includes('react') || skillLower.includes('angular') || skillLower.includes('vue') ||
                 skillLower.includes('node') || skillLower.includes('django') || skillLower.includes('flask') ||
                 skillLower.includes('spring') || skillLower.includes('express') || skillLower.includes('laravel') ||
                 skillLower.includes('asp.net') || skillLower.includes('jquery') || skillLower.includes('bootstrap')) {
        categorized.frameworks.push(skill);
      } else if (skillLower.includes('git') || skillLower.includes('docker') || skillLower.includes('aws') ||
                 skillLower.includes('jenkins') || skillLower.includes('kubernetes') || skillLower.includes('jira') ||
                 skillLower.includes('agile') || skillLower.includes('scrum') || skillLower.includes('devops') ||
                 skillLower.includes('ci/cd') || skillLower.includes('terraform') || skillLower.includes('ansible')) {
        categorized.tools.push(skill);
      } else if (skillLower.includes('ai') || skillLower.includes('ml') || skillLower.includes('tensorflow') ||
                 skillLower.includes('pytorch') || skillLower.includes('scikit') || skillLower.includes('opencv') ||
                 skillLower.includes('deep learning') || skillLower.includes('neural networks') || skillLower.includes('nlp') ||
                 skillLower.includes('computer vision') || skillLower.includes('data science') || skillLower.includes('statistics')) {
        categorized.ai_ml_technologies.push(skill);
      } else {
        // For business/soft skills, add to tools category
        categorized.tools.push(skill);
      }
    });

    return categorized;
  };

  // Get skills data - prioritize structured skills, fallback to categorized key_skills
  // For templates, show empty skills. For uploaded resumes, show the skills data.
  const getSkillsData = () => {
    // If this is a template and no user data has been entered, return empty skills
    if (resumeData.isTemplate !== false && 
        (!resumeData.full_name || resumeData.full_name.trim() === '') &&
        (!resumeData.email || resumeData.email.trim() === '') &&
        (!resumeData.phone || resumeData.phone.trim() === '')) {
      return {
        programming_languages: [],
        frameworks: [],
        tools: [],
        ai_ml_technologies: []
      };
    }
    
    if (resumeData.skills && Object.keys(resumeData.skills).length > 0) {
      return resumeData.skills;
    }
    
    if (resumeData.key_skills && Array.isArray(resumeData.key_skills) && resumeData.key_skills.length > 0) {
      return categorizeSkills(resumeData.key_skills);
    }
    
    return {
      programming_languages: [],
      frameworks: [],
      tools: [],
      ai_ml_technologies: []
    };
  };

  // Auto-convert key_skills to structured skills when component mounts or resumeData changes
  // Only convert if this is NOT a template (i.e., user has uploaded a resume)
  useEffect(() => {
    if (resumeData.key_skills && Array.isArray(resumeData.key_skills) && resumeData.key_skills.length > 0 && 
        (!resumeData.skills || Object.keys(resumeData.skills).length === 0) &&
        resumeData.isTemplate === false) {
      const categorizedSkills = categorizeSkills(resumeData.key_skills);
      updateField('skills', categorizedSkills);
    }
  }, [resumeData.key_skills, resumeData.skills, resumeData.isTemplate]);
  


  




  const addProject = () => {
    updateField("projects", [...(resumeData.projects || []), {
        title: "",
        name: "", // Keep for compatibility
        description: "",
        link: "",
        year: "",
        technologies: [],
        tech_stack: [], // Add tech_stack for compatibility
        github_link: "" // Add github_link for compatibility
    }]);
  };

  const addWebsite = () => {
    if (newWebsite.trim()) {
      updateField("websites", [...(resumeData.websites || []), newWebsite.trim()]);
      setNewWebsite("");
    }
  };

  const removeWebsite = (index) => {
    const updatedWebsites = [...(resumeData.websites || [])];
    updatedWebsites.splice(index, 1);
    updateField("websites", updatedWebsites);
  };

  const addTechnologyToProject = (projectIndex) => {
    if (newTechnology.trim()) {
      const updatedProjects = [...(resumeData.projects || [])];
      updatedProjects[projectIndex].technologies = [
        ...(updatedProjects[projectIndex].technologies || []),
        newTechnology.trim()
      ];
      updateField("projects", updatedProjects);
      setNewTechnology("");
    }
  };

  const updateField = (field, data) => {
    if (field === 'skills') {
      // Updating skills
    }
    if (field === 'projects') {
      // Updating projects
    }
    onUpdate({ ...resumeData, [field]: data });
  };

  const handleArrayField = (field, value) => {
    updateField(field, splitLines(value));
  };

  const highlightSections = Array.isArray(resumeData.key_bullet_sections)
    ? resumeData.key_bullet_sections
    : [];

  const updateHighlightSection = (index, payload) => {
    const updated = highlightSections.map((section, i) =>
      i === index ? { ...section, ...payload } : section
    );
    updateField('key_bullet_sections', updated);
  };

  const handleHighlightItemsChange = (index, value) => {
    updateHighlightSection(index, { items: splitLines(value) });
  };

  const addHighlightSection = () => {
    updateField('key_bullet_sections', [
      ...highlightSections,
      { title: '', items: [] },
    ]);
  };

  const removeHighlightSection = (index) => {
    const updated = highlightSections.filter((_, i) => i !== index);
    updateField('key_bullet_sections', updated);
  };

  const handlePersonalChange = (field, value) => {
    onUpdate({ ...resumeData, [field]: value });
  };



  const addCert = () => {
    if (newCert.trim()) {
      updateField("certifications", [...(resumeData.certifications || []), newCert.trim()]);
      setNewCert("");
    }
  };

  const removeCert = (index) => {
    updateField("certifications", resumeData.certifications.filter((_, i) => i !== index));
  };

  const addEducation = () => {
    updateField("education", [...(resumeData.education || []), {
      degree: "",
      field_of_study: "",
      institute: "",
      location: "",
      start_year: "",
      end_year: "",
      graduation_date: "",
      cgpa: ""
    }]);
  };

  const updateEducation = (index, field, value) => {
    const updated = [...(resumeData.education || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("education", updated);
  };

  const removeEducation = (index) => {
    updateField("education", resumeData.education.filter((_, i) => i !== index));
  };

  const updateProject = (index, key, value) => {
  const updated = [...resumeData.projects];
  updated[index][key] = value;
    
    // Maintain data consistency across templates
    if (key === 'technologies') {
      updated[index].tech_stack = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
    }
    if (key === 'link') {
      updated[index].github_link = value;
    }
    
  onUpdate({ ...resumeData, projects: updated });
};

  const removeProject = (index) => {
    updateField("projects", resumeData.projects.filter((_, i) => i !== index));
  };

  const baseSections = [
    { id: 'contact', label: 'Contact' },
    { id: 'summary', label: 'Summary' },
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'education', label: 'Education' },
    { id: 'certifications', label: 'Certifications' },
    { id: 'projects', label: 'Projects' },
    { id: 'achievements', label: 'Achievements' }
  ];
  const dynamicSections = getDynamicSections ? getDynamicSections() : [];
  const sections = [
    ...baseSections,
    ...(templateId === 'ats' ? [{ id: 'ats-highlights', label: 'Highlights' }] : []),
    ...dynamicSections,
  ];


  return (
    <div className="resume-form" style={{ marginTop: 0, paddingTop: 0 }}>
      <div className="form-content" style={{ marginTop: 0, paddingTop: 0 }}>
        <div className="form-header">
          <h2 className="form-title">Resume Editor</h2>
        </div>
        <SectionSelector
          sections={sections}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
        <div className="section-form">
          {activeSection === 'contact' && (
  <div className="tab-panel">
    <h3 className="section-title">Contact Information</h3>
    <div className="form-group-grid">

      <FormInput
        label="Full Name"
        name="full_name"
        value={resumeData?.full_name || ""}
        onChange={(value) => handlePersonalChange("full_name", value)}
        onFocus={onFieldFocus}
        onBlur={onFieldBlur}
        placeholder="Ryan Monroe"
        error={validationErrors.full_name}
      />

      <FormInput
        label="Email Address"
        name="email"
        type="email"
        value={resumeData?.email || ""}
        onChange={(value) => handlePersonalChange("email", value)}
        onFocus={onFieldFocus}
        onBlur={onFieldBlur}
        placeholder="ryanmonroe47@gmail.com"
        maxLength={60}
        error={validationErrors.email}
      />

      <FormInput
        label="Phone Number"
        name="phone"
        value={resumeData?.phone || ""}
        onChange={(value) => handlePersonalChange("phone", value)}
        onFocus={onFieldFocus}
        onBlur={onFieldBlur}
        placeholder="+1 (302) 559-2291"
        maxLength={20}
        error={validationErrors.phone}
      />

      <FormInput
        label="Location"
        name="location"
        value={resumeData?.location || ""}
        onChange={(value) => handlePersonalChange("location", value)}
        onFocus={onFieldFocus}
        onBlur={onFieldBlur}
        placeholder="Wilmington, DE"
        maxLength={50}
        error={validationErrors.location}
      />

      <FormInput
        label="GitHub"
        name="github"
        value={resumeData?.github || ""}
        onChange={(value) => handlePersonalChange("github", value)}
        onFocus={onFieldFocus}
        onBlur={onFieldBlur}
        placeholder="https://github.com/yourusername"
      />

      <FormInput
        label="LinkedIn"
        name="linkedin"
        value={resumeData?.linkedin || ""}
        onChange={(value) => handlePersonalChange("linkedin", value)}
        onFocus={onFieldFocus}
        onBlur={onFieldBlur}
        placeholder="https://linkedin.com/in/yourprofile"
      />

      {templateId === 'ats' && (
        <FormInput
          label="WhatsApp"
          name="whatsapp"
          value={resumeData?.whatsapp || ""}
          onChange={(value) => handlePersonalChange("whatsapp", value)}
          onFocus={onFieldFocus}
          onBlur={onFieldBlur}
          placeholder="WhatsApp: +91 99164-80004"
        />
      )}

      <FormInput
        label="LeetCode"
        name="leetcode"
        value={resumeData?.leetcode || ""}
        onChange={(value) => handlePersonalChange("leetcode", value)}
        onFocus={onFieldFocus}
        onBlur={onFieldBlur}
        placeholder="https://leetcode.com/yourusername"
      />

      <FormInput
        label="HackerRank"
        name="hackerrank"
        value={resumeData?.hackerrank || ""}
        onChange={(value) => handlePersonalChange("hackerrank", value)}
        onFocus={onFieldFocus}
        onBlur={onFieldBlur}
        placeholder="https://www.hackerrank.com/yourusername"
      />

    </div>

    <WebsiteLinksInput
      websites={resumeData.websites || []}
      newWebsite={newWebsite}
      setNewWebsite={setNewWebsite}
      onAdd={addWebsite}
      onRemove={removeWebsite}
    />
  </div>
)}

          


          {activeSection === 'summary' && (
  <div className="tab-panel">
    <h3 className="section-title">Professional Summary</h3>

    {templateId === 'ats' && (
      <>
        <div className="form-group-grid">
          <div className="form-group">
            <label>Primary Tagline</label>
            <textarea
              rows={2}
              value={resumeData?.tagline_primary || ''}
              onChange={(e) => updateField('tagline_primary', e.target.value)}
              placeholder="[ Global AI Corporate Trainer ... ]"
            />
          </div>
          <div className="form-group">
            <label>Secondary Tagline</label>
            <textarea
              rows={2}
              value={resumeData?.tagline_secondary || ''}
              onChange={(e) => updateField('tagline_secondary', e.target.value)}
              placeholder="[ IT Professional with 20+ years’ experience ... ]"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Summary Introduction</label>
          <textarea
            rows={6}
            value={resumeData?.summary_intro || ''}
            onChange={(e) => updateField('summary_intro', e.target.value)}
            placeholder="With a proven track record..."
          />
        </div>

        <div className="form-group">
          <label>Whether You're Looking To (one per line)</label>
          <textarea
            rows={4}
            value={newlineJoin(resumeData?.looking_to_items)}
            onChange={(e) => handleArrayField('looking_to_items', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Expertise Paragraph</label>
          <textarea
            rows={4}
            value={resumeData?.expertise_paragraph || ''}
            onChange={(e) => updateField('expertise_paragraph', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Collaboration Paragraph</label>
          <textarea
            rows={4}
            value={resumeData?.collaboration_paragraph || ''}
            onChange={(e) => updateField('collaboration_paragraph', e.target.value)}
          />
        </div>

        <div className="form-group-grid">
          <div className="form-group">
            <label>Call to Action - Phone</label>
            <input
              type="text"
              value={resumeData?.call_to_action_phone || ''}
              onChange={(e) => updateField('call_to_action_phone', e.target.value)}
              placeholder="📞 Let’s talk about how..."
            />
          </div>
          <div className="form-group">
            <label>Call to Action - Email</label>
            <input
              type="text"
              value={resumeData?.call_to_action_email || ''}
              onChange={(e) => updateField('call_to_action_email', e.target.value)}
              placeholder="📧 Reach out to me today!"
            />
          </div>
        </div>
      </>
    )}

    <FormTextarea
      label=""
      name="professional_summary"
      value={resumeData?.professional_summary || ""}
      onChange={(value) => updateField("professional_summary", value)}
      onFocus={onFieldFocus}
      onBlur={onFieldBlur}
      placeholder="e.g. Results-focused AI/ML engineer with proven experience..."
      rows={6}
      maxLength={1000}
      error={validationErrors.professional_summary}
    />
  </div>
)}


{activeSection === 'experience' && (
  <div className="tab-panel">
    <ExperiencesInput
      experiences={resumeData.relevant_experience}
      onChange={(updated) => updateField("relevant_experience", updated)}
      errors={validationErrors.relevant_experience}
      onFieldFocus={onFieldFocus}
      onFieldBlur={onFieldBlur}
      title="Relevant Experience"
      addLabel="Add Experience"
      positionPlaceholder="Director of Operations"
      companyPlaceholder="The Ultimate Shave"
      locationPlaceholder="Glenn Mills, PA"
      startDatePlaceholder="2021-10"
      endDatePlaceholder="Present"
      pointsPlaceholder="Describe your responsibilities..."
      positionMaxLength={70}
      companyMaxLength={70}
      locationMaxLength={50}
      startDateMaxLength={20}
      endDateMaxLength={20}
      pointsMaxLength={300}
    />
  </div>
)}

{activeSection === 'education' && (
  <div className="tab-panel">
    <div className="section-header">
      <h3 className="section-title">Education</h3>
      <AddButton onClick={addEducation}>+ Add Education</AddButton>
    </div>
    {(resumeData.education || []).map((edu, index) => {
      const eduErrors = validationErrors.education?.[index] || {};
      return (
        <div key={index} className="education-card">
          <div className="card-header">
            <h4>Education {index + 1}</h4>
            <RemoveButton onClick={() => removeEducation(index)} ariaLabel={`Remove education ${index + 1}`} />
          </div>
          <div className="card-content">

            <div className="form-group">
              <label>Degree</label>
              <input 
                type="text"
                value={edu.degree || ""}
                onChange={(e) => updateEducation(index, "degree", e.target.value)}
                placeholder="Bachelor of Technology"
              />
            </div>

            <div className="form-group">
              <label>Field of Study</label>
              <input 
                type="text"
                value={edu.field_of_study || ""}
                onChange={(e) => updateEducation(index, "field_of_study", e.target.value)}
                placeholder="Electronics and Communication Engineering"
              />
            </div>

            <div className="form-group">
              <label>Institute</label>
  <input 
    type="text"
    value={edu.institute || ""}  // ✅ Correct key
    onChange={(e) => updateEducation(index, "institute", e.target.value)}  // ✅ Correct update path
    placeholder="Siddhartha Institute of Technology and Sciences"
  />
            </div>

            <div className="form-group">
              <label>Location</label>
              <input 
                type="text"
                value={edu.location || ""}
                onChange={(e) => updateEducation(index, "location", e.target.value)}
                placeholder="Hyderabad, Telangana"
              />
            </div>

            <div className="form-group-grid">
              <div className="form-group">
                <label>Start Year</label>
                <input 
                  type="text"
                  value={edu.start_year || ""}
                  onChange={(e) => updateEducation(index, "start_year", e.target.value)}
                  placeholder="2020"
                />
              </div>
              <div className="form-group">
                <label>End Year</label>
                <input 
                  type="text"
                  value={edu.end_year || ""}
                  onChange={(e) => updateEducation(index, "end_year", e.target.value)}
                  placeholder="2024"
                />
              </div>
            </div>

            <div className="form-group">
              <label>CGPA</label>
              <input 
                type="text"
                value={edu.cgpa || ""}
                onChange={(e) => updateEducation(index, "cgpa", e.target.value)}
                placeholder="7.09/10"
              />
            </div>

          </div>
        </div>
      );
    })}
  </div>
)}


{activeSection === 'certifications' && (
  <div className="tab-panel">
    <h3 className="section-title">Certifications</h3>
    <div className="add-skill">
      <input
        type="text"
        value={newCert}
        onChange={(e) => setNewCert(e.target.value)}
        placeholder="Add a certification"
        maxLength="100"
      />
      <button onClick={addCert}>Add</button>
    </div>
    <div className="skills-list">
      {(resumeData.certifications || []).map((cert, index) => (
        <span key={index} className="skill-tag">
          <span>
            {typeof cert === 'string' ? cert : (cert.name || cert)}
            {typeof cert === 'object' && cert.provider ? ` – ${cert.provider}` : ""}
          </span>
          <button onClick={() => removeCert(index)}>×</button>
        </span>
      ))}
    </div>
  </div>
)}


{activeSection === 'skills' && (
  <div className="tab-panel">
    <div className="section-header">
      <h3 className="section-title">Skills</h3>
    </div>



    <CategorizedSkillsInput
      skills={getSkillsData()}
      onClear={() => {
        const clearedSkills = {
          programming_languages: [],
          frameworks: [],
          tools: [],
          ai_ml_technologies: [],
        };
        updateField("skills", clearedSkills);
        // Also clear key_skills if they exist
        if (resumeData.key_skills) {
          onUpdate({ ...resumeData, skills: clearedSkills, key_skills: [] });
        }
      }}
      clearLabel="Clear"
      onChange={(updatedSkills) => {
        updateField('skills', updatedSkills);
        // Keep old behavior: clearing key_skills once structured skills exist
        if (resumeData.key_skills) {
          onUpdate({ ...resumeData, skills: updatedSkills, key_skills: [] });
        }
      }}
      categories={[
        { key: 'programming_languages', label: 'Programming Languages', placeholder: 'Add a programming language...' },
        { key: 'frameworks', label: 'Frameworks', placeholder: 'Add a framework...' },
        { key: 'tools', label: 'Tools', placeholder: 'Add a tool...' },
        { key: 'ai_ml_technologies', label: 'AI/ML Technologies', placeholder: 'Add an AI/ML technology...' },
      ]}
    />
  </div>
)}


{activeSection === 'achievements' && (
  <div className="tab-panel">
    <div className="section-header">
      <h3 className="section-title">Achievements</h3>
      <AddButton onClick={() => updateField("achievements", [...(resumeData.achievements || []), ""])}>
        + Add Achievement
      </AddButton>
    </div>
    {(resumeData.achievements || []).map((item, index) => (
      <div key={index} className="form-group" style={{ display: 'flex', gap: '10px' }}>
        <input
          type="text"
          value={item}
          onChange={(e) => {
            const updated = [...resumeData.achievements];
            updated[index] = e.target.value;
            updateField("achievements", updated);
          }}
          placeholder="E.g., GATE Qualified - 97.43 percentile"
        />
        <RemoveButton onClick={() => {
          const updated = resumeData.achievements.filter((_, i) => i !== index);
          updateField("achievements", updated);
        }} ariaLabel={`Remove achievement ${index + 1}`} />
      </div>
    ))}
  </div>
)}


{activeSection === 'projects' && (
  <div className="tab-panel">
    <div className="section-header">
      <h3 className="section-title">Projects</h3>
      <AddButton onClick={addProject}>+ Add Project</AddButton>
    </div>
    {(resumeData.projects || []).map((proj, index) => (
      <div key={index} className="experience-card">
        <div className="card-header">
          <h4>Project {index + 1}</h4>
          <RemoveButton onClick={() => removeProject(index)} ariaLabel={`Remove project ${index + 1}`} />
        </div>
        <div className="card-content">
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={proj.title || ''}
              onChange={(e) => updateProject(index, "title", e.target.value)}
              placeholder="Project Title"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={proj.description || ''}
              onChange={(e) => updateProject(index, "description", e.target.value)}
              placeholder="A short description of your project."
              rows={3}
            />
          </div>
          <div className="form-group">
            <label>Link</label>
            <input
              type="text"
              value={proj.link || ''}
              onChange={(e) => updateProject(index, "link", e.target.value)}
              placeholder="https://github.com/your/project"
            />
          </div>
          <div className="form-group">
    <label>Year</label>
    <input
      type="text"
      value={proj.year || ""}
      onChange={(e) => updateProject(index, "year", e.target.value)}
      placeholder="2025"
    />
  </div>
          <div className="form-group">
            <label>Technologies (comma separated)</label>
            <input
              type="text"
              value={Array.isArray(proj.technologies) ? proj.technologies.join(', ') : ''}
              onChange={(e) => {
                const updated = [...(resumeData.projects || [])];
                const technologies = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                updated[index] = { 
                  ...proj, 
                  technologies: technologies,
                  tech_stack: technologies // Ensure tech_stack is also updated
                };
                updateField("projects", updated);
              }}
              placeholder="React, Node.js, Firebase"
            />
          </div>
        </div>
      </div>
    ))}
  </div>
)}

{templateId === 'ats' && activeSection === 'ats-highlights' && (
  <div className="tab-panel">
    <div className="section-header" style={{ marginBottom: 16 }}>
      <h3 className="section-title">Impact Highlights</h3>
      <AddButton onClick={addHighlightSection}>
        + Add Highlight
      </AddButton>
    </div>
    {highlightSections.length === 0 && (
      <p style={{ color: '#64748b', fontSize: 14 }}>
        No highlights yet. Add one to get started.
      </p>
    )}
    {highlightSections.map((section, index) => (
      <div key={index} className="experience-card">
        <div className="card-header">
          <h4>Highlight {index + 1}</h4>
          <RemoveButton
            onClick={() => removeHighlightSection(index)}
            ariaLabel={`Remove highlight ${index + 1}`}
          />
        </div>
        <div className="card-content">
          <div className="form-group">
            <label>Highlight Title</label>
            <textarea
              rows={2}
              value={section.title || ''}
              onChange={(e) => updateHighlightSection(index, { title: e.target.value })}
              placeholder="Conducted scores of customized corporate trainings..."
            />
          </div>
          <div className="form-group">
            <label>Bullet Points (one per line)</label>
            <textarea
              rows={6}
              value={newlineJoin(section.items)}
              onChange={(e) => handleHighlightItemsChange(index, e.target.value)}
            />
          </div>
        </div>
      </div>
    ))}
  </div>
)}

          {/* DYNAMIC SECTIONS - Rendered from ResumeEditor.jsx */}
          {renderDynamicSectionForm &&
            dynamicSections.map((section) => {
              if (activeSection === section.id) {
                return renderDynamicSectionForm(section.id);
              }
              return null;
            })}

          
  
        </div>
      </div>
    </div>
  );
} 
 
export default ProfessionalResumeForm;
